import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '../utils/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Set authorization header BEFORE making the API call
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Use the new API client which includes version checking
          const response = await apiClient.get('/api/profile');
          console.log('🔐 User authenticated from token:', response.data);
          setUser(response.data);
        } catch (error) {
          console.error('🔐 Token validation failed:', error);
          
          // Check if it's a version error
          if (error.code === 'VERSION_OUTDATED') {
            console.log('🔄 Version outdated - user will be prompted to update');
            // Don't remove token yet, let version handling take care of it
          } else {
            // Remove invalid token for other errors
            localStorage.removeItem('token');
            delete apiClient.defaults.headers.common['Authorization'];
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username, password) => {
    try {
      console.log('🔐 Attempting login for:', username);
      
      const response = await apiClient.post('/api/login', {
        username,
        password
      });

      console.log('🔐 Login successful:', response.data);
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      
      // Set up axios default header for future requests
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      return { success: true };
    } catch (error) {
      console.error('🔐 Login failed:', error);
      
      // Handle version errors specifically
      if (error.code === 'VERSION_OUTDATED') {
        return { 
          success: false, 
          error: 'App update required to continue. Please update your app and try again.',
          requiresUpdate: true
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed. Please check your credentials.' 
      };
    }
  };

  const register = async (userData) => {
    try {
      console.log('🔐 Attempting registration for:', userData.username);
      
      const response = await apiClient.post('/api/register', userData);
      
      console.log('🔐 Registration successful:', response.data);
      return { success: true };
    } catch (error) {
      console.error('🔐 Registration failed:', error);
      
      // Handle version errors
      if (error.code === 'VERSION_OUTDATED') {
        return { 
          success: false, 
          error: 'App update required to continue. Please update your app and try again.',
          requiresUpdate: true
        };
      }
      
      // Extract detailed validation errors if available
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data) {
        const { error: serverError, details } = error.response.data;
        
        if (details && Array.isArray(details)) {
          // Format validation errors nicely
          errorMessage = details.map(detail => detail.message).join(', ');
        } else if (serverError) {
          errorMessage = serverError;
        }
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = () => {
    console.log('🔐 User logging out');
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const forgotPassword = async (email) => {
    try {
      console.log('🔐 Requesting password reset for:', email);
      
      const response = await apiClient.post('/api/reset-password-request', { email });
      
      console.log('🔐 Password reset request successful');
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('🔐 Password reset request failed:', error);
      
      // Handle version errors
      if (error.code === 'VERSION_OUTDATED') {
        return { 
          success: false, 
          error: 'App update required to continue. Please update your app and try again.',
          requiresUpdate: true
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to send password reset email. Please try again.' 
      };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      console.log('🔐 Attempting password reset with token');
      
      const response = await apiClient.post('/api/reset-password-confirm', {
        token,
        newPassword
      });
      
      console.log('🔐 Password reset successful');
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('🔐 Password reset failed:', error);
      
      // Handle version errors
      if (error.code === 'VERSION_OUTDATED') {
        return { 
          success: false, 
          error: 'App update required to continue. Please update your app and try again.',
          requiresUpdate: true
        };
      }
      
      return { 
        success: false, 
        error: error.response?.data?.error || 'Password reset failed. Please try again.' 
      };
    }
  };



  const value = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 